import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { getLiveSocket, announcePresence } from '../utils/liveSocket';

function OnlineStaffTracker({ user }) {
  const [teachers, setTeachers] = useState([]);
  const [onlineIds, setOnlineIds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all(
      ['teacher', 'teacher_admin_level1'].map((role) =>
        axios.get(`${API_BASE_URL}/api/users`, { params: { role } }).then((r) => r.data || [])
      )
    )
      .then(([regular, supers]) => setTeachers([...supers, ...regular]))
      .catch((err) => console.error('Failed to load staff:', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const socket = getLiveSocket();
    const handler = (ids) => setOnlineIds((ids || []).map(String));
    socket.on('online_teachers_update', handler);
    // Joins admin_room on the server (also re-joins on reconnect via announcePresence)
    if (user) announcePresence(user);
    return () => socket.off('online_teachers_update', handler);
  }, [user]);

  const online = new Set(onlineIds);
  const sorted = [...teachers].sort((a, b) => {
    const diff = Number(online.has(String(b._id))) - Number(online.has(String(a._id)));
    return diff || (a.name || '').localeCompare(b.name || '');
  });
  const onlineCount = sorted.filter((t) => online.has(String(t._id))).length;

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 w-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">👥 Staff Online Status (Teachers &amp; Super Teachers)</h3>
        <span className="text-xs font-medium px-3 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
          {onlineCount} / {teachers.length} online
        </span>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">Loading teachers…</p>
      ) : sorted.length === 0 ? (
        <p className="text-sm text-gray-400">No teachers found.</p>
      ) : (
        <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {sorted.map((t) => {
            const isOnline = online.has(String(t._id));
            return (
              <li key={t._id} className="flex items-center justify-between py-2.5 px-1 hover:bg-gray-50 rounded-lg transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white flex items-center justify-center text-sm font-semibold shrink-0">
                    {(t.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm font-medium text-gray-800 truncate">{t.name}</span>
                  {t.role === 'teacher_admin_level1' && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200 whitespace-nowrap">
                      Super Teacher
                    </span>
                  )}
                </div>
                {isOnline ? (
                  <span className="inline-flex items-center gap-2 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75 animate-ping"></span>
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500 animate-pulse"></span>
                    </span>
                    🟢 Online
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">⚪ Offline</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default OnlineStaffTracker;
